import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import useEntity from 'entities/hooks/use-entity';
import { Flex, Text, Box } from 'rebass';
import { FaRegEdit } from "react-icons/fa";
import { DragHandle, Paragraph, SortableContainer } from './components/storiesSection';
import { sortableElement } from 'react-sortable-hoc';
import RadioToggle from './components/RadioToggle';
import { useParams } from 'react-router-dom';
import { patchChapters } from 'collections/api';
import arrayMove from 'array-move';
import CollectionTagManagement from 'collections/detail/tag-management/collection';
import LoadingIcon from 'app/inline-loading';

function RenderStorySection({ firstBookId, revalidate, journalId, ...val }) {
    const [isDisabled, setIsDisabled] = useState((val.kind === 'user' && val.stories?.length === 0) || val.state === 'disabled')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        setIsDisabled((val.kind === 'user' && val.stories?.length === 0) || val.state === 'disabled')
    }, [val.kind, val.stories?.length, val.state])

    const onToggle = async (toggled) => {
        if (val.kind === 'user' && val.stories?.length === 0) return;
        setIsSubmitting(true)
        setIsDisabled(!toggled)
        const { data, status } = await patchChapters({
            id: journalId,
            chapterId: val.id,
            attributes: { data: { attributes: { state: toggled ? 'enabled' : 'disabled' } } },
        })

        if (status === 200 || status === 204 || status === 201) {
            setIsSubmitting(false)
            revalidate()
        }
    }
    if (val.childJournalWatchMeGrowPermitted === false && val.name === 'Watch Me Grow') return null;
    if (val.isMilestonePermitted === false && val.name === "Milestones") return null;
    if (val.isGrowthChartPermitted === false && val.name === 'Growth Chart') return null;
    if (val.isTalkingFamilyTreePermitted === false && val.name === 'Talking Family Tree') return null;

    return (
        <Flex
            key={val.id}
            alignItems="center"
            bg="#fff"
            sx={{
                borderRadius: '10px',
                boxShadow: '4px 4px 4px 0 rgba(133, 79, 155, 0.05)',
                gap: '10px',
                opacity: isDisabled ? 0.5 : 1
            }}
            m="10px"
            p={[1, "10px 20px"]}
        >
            {isDisabled ? <Box minWidth={24} /> : <DragHandle />}
            <Box>
                <Paragraph variant="paragraph" width={['200px', 'fit-content', 'fit-content']}>
                    {val.name}{val.kind === 'system' ? '' : `(${val.stories?.length})`}
                </Paragraph>
            </Box>
            <Flex ml="auto" alignItems="center" sx={{ gap: '10px' }}>

                {isSubmitting ?
                    <LoadingIcon style={{
                        width: '20px',
                        height: '20px',
                    }} />
                    : <RadioToggle
                        isSubmitting={isSubmitting}
                        isDisabled={isDisabled}
                        initial={!isDisabled}
                        onToggle={onToggle}
                        noToggle={(val.kind === 'user' && val.stories?.length === 0)}
                        style={{ cursor: (val.kind === 'user' && val.stories?.length === 0) ? 'not-allowed' : 'pointer' }}
                    />}
            </Flex>
        </Flex>
    )
}

const SortableItem = sortableElement((props) => (
    <li>
        <RenderStorySection {...props} />
    </li>
));

const OrganizeChapters = ({ firstBookId, structuredStories, revalidate, chapters }) => {
    const params = useParams()
    const { id: journalId } = params;
    const { entity: paramEntity } = useEntity(params);

    const childJournalWatchMeGrowPermitted = paramEntity?.['child-journal-watch-me-grow-permitted'];
    const isMilestonePermitted = paramEntity?.['child-journal-milestones-permitted'];
    const isGrowthChartPermitted = paramEntity?.['child-journal-growth-chart-permitted'];
    const isTalkingFamilyTreePermitted = paramEntity?.['child-journal-talking-family-tree-permitted'];

    const type = 'book';
    const { entity: collection } = useEntity({ type, id: firstBookId });

    const [list, setList] = useState([])
    const [showChapterEditor, setShowChapterEditor] = useState(false)

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const next = arrayMove(list, oldIndex, newIndex);
        const updatedList = next.map((ch, index) => ({ ...ch, position: index }))

        setList(updatedList)

        updatedList.map((({ stories, ...tag }) => patchChapters({
            id: journalId,
            chapterId: tag.id,
            attributes: { data: { attributes: { ...tag } } },
        })
        ))
    };

    useEffect(() => {
        setList(structuredStories.filter(({ name }) => name !== 'Stories that need a Chapter').sort((a, b) => a.position - b.position))
    }, [structuredStories])

    if (isEmpty(collection)) return false;

    return (
        <Box maxWidth={539} m="auto">
            <Text fontSize="16px" fontFamily="Poppins" fontWeight={400} lineHeight="150%" letterSpacing="-0.32px" mb={3} mt={2} color="#737373">
                Drag chapters & special sections into the order you want in your book. You can make changes later.
            </Text>
            <SortableContainer useDragHandle onSortEnd={onSortEnd}>
                {(list || []).map((val, i) => (
                    <SortableItem
                        childJournalWatchMeGrowPermitted={childJournalWatchMeGrowPermitted}
                        isMilestonePermitted={isMilestonePermitted}
                        isGrowthChartPermitted={isGrowthChartPermitted}
                        isTalkingFamilyTreePermitted={isTalkingFamilyTreePermitted}
                        revalidate={revalidate}
                        journalId={journalId}
                        firstBookId={firstBookId}
                        key={`chapter-item-${val.id}`}
                        {...val}
                        index={i}
                    />
                ))}
            </SortableContainer>
            {showChapterEditor && <CollectionTagManagement
                isFromBookCreation
                revalidate={revalidate}
                chapters={Object.values(chapters).filter(({ kind }) => kind !== 'system').map(({ stories, ...item }) => ({
                    attributes: item
                }))}
                journalId={journalId}
                onClose={() => {
                    revalidate()
                    setShowChapterEditor(false);
                }}
            />}

            <Flex px={3} py={2} justifyContent="center" m="auto" alignItems="center" sx={{ cursor: 'pointer' }} width="max-content"
                onClick={() => setShowChapterEditor(true)}
            >
                <FaRegEdit size={20} color="#64748B" />
                <Text minWidth={130} ml="12px" fontFamily="Poppins" fontWeight={500} lineHeight="20px" color="#64748B">
                    Edit Chapters Titles
                </Text>
            </Flex>
        </Box>
    );
};

export default OrganizeChapters;
